/**
 * Session persistence. The storage keys match the previous console so admins who are
 * already signed in keep their session when they switch to this app.
 */
const TOKEN_KEY = 'accessToken';
const TOKEN_CREATED_KEY = 'tokenCreateDate';

/** Days after which an active session silently exchanges its token (GET /api/admins/token). */
export const TOKEN_REFRESH_AFTER_DAYS = 7;

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export const session = {
  token(): string | null {
    return safeGet(TOKEN_KEY);
  },

  createdAt(): number | null {
    const raw = safeGet(TOKEN_CREATED_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  },

  set(token: string, now: number = Date.now()): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_CREATED_KEY, String(now));
    } catch {
      /* storage unavailable: the in-memory listeners still update */
    }
    listeners.forEach((l) => l(token));
  },

  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_CREATED_KEY);
    } catch {
      /* ignore */
    }
    listeners.forEach((l) => l(null));
  },

  /** True when the token is old enough to be exchanged for a fresh one. */
  needsRefresh(now: number = Date.now()): boolean {
    const created = this.createdAt();
    return created !== null && now - created > TOKEN_REFRESH_AFTER_DAYS * 24 * 3600 * 1000;
  },

  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};
